import { acceptGoTuTermsService } from '~/domain/AcceptGoTuTerms';
import { ConsentsService, OfficeTnCTypeEnum } from '~/domain/Consents';
import { selectedOfficeService } from '~/domain/SelectedOffice';
import { captureException } from '~/services/datadog';
import { UserTypeEnum } from '~/models';
import { SignUpAdapters } from '../adapters';
import { createUser } from '../clients/CreateUser';
import { refreshSession } from '../clients/RefreshSession';
import { setUserType } from '../clients/SetUserType';
import {
  EmailAlreadyInUseError,
  SignUpApiError,
  SignUpRateLimitedError,
} from '../errors';
import type { ISignUpRequest, ISignUpResponse } from '../types/SignUp';

const signUp = async (input: ISignUpRequest): Promise<ISignUpResponse> => {
  try {
    const createUserResponse = await createUser(SignUpAdapters.toCreateUserPayload(input));

    await setUserType({
      userType: UserTypeEnum.DNT,
      accessToken: createUserResponse.accessToken,
    });

    // Exchange the short-lived signup token (~120s) for a full-session token
    // so the wizard steps don't 401 mid-flow.
    const refreshed = await refreshSession({ refreshToken: createUserResponse.refreshToken });

    // Persist T&C acceptance on the BE (DSO structure FF is permanently off).
    // Override Authorization with the refreshed token — the redux store isn't
    // hydrated yet, so the default `getAuthHeaders()` path wouldn't see it.
    await acceptGoTuTermsService.acceptGoTuTerms({ accessToken: refreshed.accessToken });

    // Also accept the office_tnc consent in credential-service. The scroll-to-
    // accept popup already gated the toggle on the client side, so the
    // signup flow records the same intent on the BE here — without this the
    // user lands on the dashboard and immediately gets the "we've updated our
    // terms" modal. Best-effort: a failure here mustn't block signup.
    try {
      const me = await selectedOfficeService.getSelectedOffice({
        accessToken: refreshed.accessToken,
      });
      await ConsentsService.acceptOfficeConsentContent({
        officeId: me.id,
        code: OfficeTnCTypeEnum.OFFICE_TNC,
        accessToken: refreshed.accessToken,
      });
    } catch (consentError) {
      captureException(consentError);
    }

    return SignUpAdapters.toSignUpResponse(refreshed);
  } catch (error) {
    // Specific conditions first, then a catch-all that surfaces whatever
    // message the BE wrote (instead of "Request failed with status code …").
    const typedError =
      EmailAlreadyInUseError.fromAxiosError(error) ??
      SignUpRateLimitedError.fromAxiosError(error) ??
      SignUpApiError.fromAxiosError(error);

    throw typedError ?? error;
  }
};

export const SignUpService = {
  signUp,
};
